/**
 * useSSENotifications
 *
 * Real-time notification delivery using Server-Sent Events (SSE).
 * This is the standard approach used by Facebook, GitHub, Notion, etc.
 *
 * Architecture:
 *  • The backend Celery worker processes events (auction end, payment credited, etc.)
 *    and pushes them to a Redis channel.
 *  • FastAPI SSE endpoint (/notifications/stream) reads from that channel and
 *    streams events to connected clients over a single long-lived HTTP connection.
 *  • The browser never polls — the server pushes exactly when something happens.
 *
 * Fallback strategy (if SSE endpoint not yet live):
 *  • Falls back to "smart polling" — only fetches when the user returns to the tab
 *    (visibilitychange) or the window regains focus. Zero background traffic.
 *
 * Reconnect: exponential backoff (1 s → 2 s → 4 s … capped at 60 s).
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAppSelector } from './redux';
import api from '../services/api';
import {
  getShownNotificationIds,
  isNotificationFromCurrentSession,
  markNotificationsShown,
  MODAL_NOTIFICATION_GUARD_KEY,
  normalizeNotificationId,
  wasNotificationShown,
} from '../utils/notificationDisplayGuard';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SSENotification {
  id: number;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  data?: Record<string, unknown>;
}

interface UseSSENotificationsOptions {
  /** Called for every new notification delivered via SSE or fallback poll */
  onNotifications: (notifications: SSENotification[]) => void;
  /** SSE stream path, relative to the API base (e.g. '/notifications/stream') */
  streamPath?: string;
  /** Fallback fetch path used when SSE is unavailable */
  fetchPath?: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

function normalizeNotificationResponse(payload: unknown): SSENotification[] {
  if (Array.isArray(payload)) {
    return payload as SSENotification[];
  }

  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { notifications?: unknown }).notifications)
  ) {
    return (payload as { notifications: SSENotification[] }).notifications;
  }

  return [];
}

export function useSSENotifications({
  onNotifications,
  streamPath = '/notifications/stream',
  fetchPath = '/notifications/',
}: UseSSENotificationsOptions): void {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const esRef = useRef<EventSource | null>(null);
  const retryDelayRef = useRef<number>(1000);         // start at 1 s
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sseWorkingRef = useRef<boolean>(false);
  const hasPrimedInitialUnreadRef = useRef(false);
  const sessionStartedAtRef = useRef(Date.now());
  const onNotificationsRef = useRef(onNotifications); // stable ref — avoids stale closure
  onNotificationsRef.current = onNotifications;

  // ─── Fallback: fetch unread notifications once, filter already-shown ────────
  const fetchFallback = useCallback(async (primeOnly = false) => {
    if (!isAuthenticated) return;
    try {
      const { data } = await api.get<unknown>(fetchPath, {
        params: { _t: Date.now() },
      });

      const notifications = normalizeNotificationResponse(data);
      const shown = getShownNotificationIds(MODAL_NOTIFICATION_GUARD_KEY);
      const unreadUnshown = notifications.filter((n) => {
        const id = normalizeNotificationId(n.id);
        return id && !n.is_read && !shown.has(id);
      });

      if (primeOnly) {
        markNotificationsShown(
          unreadUnshown.map((notification) => notification.id),
          MODAL_NOTIFICATION_GUARD_KEY,
        );
        return;
      }

      const fresh = unreadUnshown.filter((notification) => (
        isNotificationFromCurrentSession(notification.created_at, sessionStartedAtRef.current)
      ));
      const stale = unreadUnshown.filter((notification) => !fresh.includes(notification));

      if (stale.length > 0) {
        markNotificationsShown(
          stale.map((notification) => notification.id),
          MODAL_NOTIFICATION_GUARD_KEY,
        );
      }

      if (fresh.length > 0) onNotificationsRef.current(fresh);
    } catch {
      // Silent — fallback should never break the app
    }
  }, [isAuthenticated, fetchPath]);

  // ─── Build the full SSE URL (including auth token as query param) ───────────
  const buildSSEUrl = useCallback((): string => {
    const base =
      process.env.REACT_APP_API_URL ??
      `${window.location.protocol}//${window.location.hostname}:8000`;
    const clean = base.replace(/\/$/, '');
    const token = localStorage.getItem('accessToken') ?? '';
    const path = streamPath.startsWith('/') ? streamPath : `/${streamPath}`;
    return `${clean}/api/v1${path}?token=${encodeURIComponent(token)}`;
  }, [streamPath]);

  // ─── SSE connection ──────────────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (!isAuthenticated) return;
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    let es: EventSource;
    try {
      es = new EventSource(buildSSEUrl());
    } catch {
      // EventSource not supported (very rare) — run fallback only
      fetchFallback();
      return;
    }
    esRef.current = es;

    // Generic message (server sends: data: <JSON>)
    es.onmessage = (event) => {
      sseWorkingRef.current = true;
      retryDelayRef.current = 1000; // reset backoff on successful message
      try {
        const payload = JSON.parse(event.data) as SSENotification | SSENotification[];
        const notifications = Array.isArray(payload) ? payload : [payload];
        const fresh = notifications.filter((n) => (
          !n.is_read &&
          isNotificationFromCurrentSession(n.created_at, sessionStartedAtRef.current) &&
          !wasNotificationShown(n.id, MODAL_NOTIFICATION_GUARD_KEY)
        ));
        if (fresh.length > 0) onNotificationsRef.current(fresh);
      } catch {
        // Malformed JSON — ignore
      }
    };

    // Named event types the backend can emit (e.g. event: auction_won)
    const namedTypes = ['auction_won', 'auction_win', 'new_earning', 'date_completed', 'payout_approved', 'gift_received', 'gift_sent_confirmation'];
    namedTypes.forEach((type) => {
      es.addEventListener(type, (event) => {
        sseWorkingRef.current = true;
        retryDelayRef.current = 1000;
        try {
          const notif = JSON.parse((event as MessageEvent).data) as SSENotification;
          if (
            !notif.is_read &&
            isNotificationFromCurrentSession(notif.created_at, sessionStartedAtRef.current) &&
            !wasNotificationShown(notif.id, MODAL_NOTIFICATION_GUARD_KEY)
          ) {
            onNotificationsRef.current([notif]);
          }
        } catch {/* ignore */}
      });
    });

    // Heartbeat — server sends a ping every ~30 s to keep the connection alive
    es.addEventListener('ping', () => {
      sseWorkingRef.current = true;
    });

    es.onerror = () => {
      es.close();
      esRef.current = null;

      if (!sseWorkingRef.current) {
        // SSE endpoint probably doesn't exist yet — activate fallback mode
        // Run an immediate fetch then rely on visibility/focus events
        fetchFallback();
      }

      // Exponential backoff reconnect (1 s → 2 → 4 … → 60 s cap)
      retryTimerRef.current = setTimeout(() => {
        retryDelayRef.current = Math.min(retryDelayRef.current * 2, 60_000);
        connect();
      }, retryDelayRef.current);
    };
  }, [isAuthenticated, buildSSEUrl, fetchFallback]);

  // ─── Visibility / focus fallback polling ─────────────────────────────────────
  // Only fires when the user actually returns to the tab — zero background traffic
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible' && hasPrimedInitialUnreadRef.current) {
        fetchFallback();
      }
    };
    const handleFocus = () => {
      if (hasPrimedInitialUnreadRef.current) {
        fetchFallback();
      }
    };

    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchFallback]);

  // ─── Mount / unmount ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      esRef.current?.close();
      esRef.current = null;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      hasPrimedInitialUnreadRef.current = false;
      sseWorkingRef.current = false;
      return;
    }

    let cancelled = false;
    sessionStartedAtRef.current = Date.now();
    hasPrimedInitialUnreadRef.current = false;
    sseWorkingRef.current = false;
    retryDelayRef.current = 1000;

    const startRealtimeDelivery = async () => {
      // Prime existing unread notifications so old backlog never replays popups/sounds
      // on login. The bell/list still fetches unread counts separately.
      await fetchFallback(true);
      if (cancelled) return;
      hasPrimedInitialUnreadRef.current = true;
      connect();
    };

    startRealtimeDelivery();

    return () => {
      cancelled = true;
      esRef.current?.close();
      esRef.current = null;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [isAuthenticated, connect, fetchFallback]);
}
